import { UserMinus } from "lucide-mui";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useOrgMembers } from "../hooks/resources.ts";
import { api, type OrgMember } from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";
import { useAuth } from "../contexts/AuthContext.tsx";
import EmptyTableRow from "./table/EmptyTableRow.tsx";
import FilterBar from "./filters/FilterBar.tsx";
import type { FilterSpec } from "./filters/specs.ts";
import { useFilters, useLocalFilterState } from "./filters/useFilters.ts";
import ScrollTable from "./table/ScrollTable.tsx";
import TableCard from "./table/TableCard.tsx";
import FormattedDate from "./FormattedDate";
import { UserCell } from "./IdentityCell.tsx";
import { OrgRoleChips, OrgRolesMenu } from "./OrgRoles.tsx";
import RelativeTime from "./RelativeTime.tsx";
import RowActions from "./RowActions.tsx";
import UserGroupChips from "./UserGroupChips";
import { formatNumber } from "../utils/format.ts";

interface OrgMembersTableProps {
  organizationId: string;
  /** When true, roles are editable and members can be removed. */
  canManage: boolean;
  /** When true, show a per-user group column with add/remove/create controls. */
  canManageGroups?: boolean;
  /** Drop the table's own border, for a table inside another box (an `AccordionCard`). */
  flush?: boolean;
}

// Search only.
const NO_FILTERS: FilterSpec[] = [];

const memberName = (m: OrgMember) => m.user_display_name || m.user_email;

function matches(m: OrgMember, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [m.user_display_name, m.user_email, ...(m.groups ?? []).map((g) => g.name)].some((s) =>
    s?.toLowerCase().includes(q),
  );
}

/**
 * Members table for a single organization. Fetches members via
 * `keys.organizationMembership.byOrg` so multiple mounts share one cache entry.
 * Used by both the org detail page (site admin) and the org Members page.
 */
function OrgMembersTable({ organizationId, canManage, canManageGroups = false, flush = false }: OrgMembersTableProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const filterState = useLocalFilterState();
  const filters = useFilters(NO_FILTERS, {
    state: filterState,
    search: { param: "q", placeholder: canManageGroups ? "Search name, email or group" : "Search name or email" },
  });
  const search = filters.get("q") ?? "";

  const { data: members = [], isLoading } = useOrgMembers(organizationId);
  const visible = members.filter((m) => matches(m, search));

  // Candidate groups for the "add existing" menu; per-user membership comes from
  // each member's `groups` field. Key sits under keys.userGroup.all so group mutations
  // refresh it automatically.
  const { data: allGroups = [] } = useQuery({
    queryKey: keys.userGroup.list(organizationId),
    queryFn: () => api.getUserGroups(organizationId),
    enabled: canManageGroups && !!organizationId,
  });

  const updateMemberMutation = useMutation({
    mutationFn: ({ userId, roles }: { userId: string; roles: string[] }) =>
      api.updateOrgMember(organizationId, userId, { roles }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.organizationMembership.byOrg(organizationId),
      });
      toast.success("Roles updated");
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => api.removeOrgMember(organizationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.organizationMembership.byOrg(organizationId),
      });
      toast.success("Member removed");
    },
  });

  const columnCount = 4 + (canManageGroups ? 1 : 0) + (canManage ? 1 : 0);
  const membersWord = members.length === 1 ? "member" : "members";

  return (
    <TableCard
      loading={isLoading}
      sx={flush ? { border: 0, borderRadius: 0, bgcolor: "transparent" } : undefined}
      toolbar={
        <FilterBar
          filters={filters}
          // The page heading already counts the members; the bar says what a search leaves.
          count={search.trim() ? `${formatNumber(visible.length)} of ${formatNumber(members.length)} ${membersWord}` : undefined}
        />
      }
    >
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell>Member</TableCell>
            <TableCell>Roles</TableCell>
            {canManageGroups && <TableCell>Groups</TableCell>}
            <TableCell>Last active</TableCell>
            <TableCell>Joined</TableCell>
            {canManage && <TableCell padding="checkbox" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((m) => (
            <TableRow key={m.id} hover>
              <TableCell>
                <UserCell
                  displayName={m.user_display_name}
                  email={m.user_email}
                  pictureUrl={m.user_picture_url}
                  tag={m.user_id === user?.id ? "You" : undefined}
                />
              </TableCell>
              <TableCell>
                {canManage ? (
                  <OrgRolesMenu
                    value={m.roles}
                    memberName={memberName(m)}
                    disabled={updateMemberMutation.isPending}
                    onChange={(roles) => updateMemberMutation.mutate({ userId: m.user_id, roles })}
                  />
                ) : (
                  <OrgRoleChips roles={m.roles} />
                )}
              </TableCell>
              {canManageGroups && (
                <TableCell>
                  <UserGroupChips
                    orgId={organizationId}
                    userId={m.user_id}
                    userName={memberName(m)}
                    memberGroups={m.groups ?? []}
                    allGroups={allGroups}
                    canManage={canManageGroups}
                  />
                </TableCell>
              )}
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <RelativeTime value={m.user_last_seen} />
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                <FormattedDate value={m.created_at} />
              </TableCell>
              {canManage && (
                <TableCell padding="checkbox" align="right">
                  <RowActions
                    label={memberName(m)}
                    actions={[
                      {
                        label: "Remove from organization",
                        icon: <UserMinus fontSize="small" />,
                        destructive: true,
                        onClick: () => removeMemberMutation.mutateAsync(m.user_id),
                        confirm: {
                          title: "Remove member",
                          confirmLabel: "Remove",
                          body: (
                            <>
                              Remove <strong>{memberName(m)}</strong> from this organization? They lose
                              access to its tenants.
                            </>
                          ),
                        },
                      },
                    ]}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
          {visible.length === 0 && (
            <EmptyTableRow colSpan={columnCount}>
              {members.length === 0 ? "No members yet" : "No members match your search"}
            </EmptyTableRow>
          )}
        </TableBody>
      </ScrollTable>
    </TableCard>
  );
}

export default OrgMembersTable;
